import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { buttonVariants } from './button-variants';
import { Spinner } from './spinner';

interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            loading = false,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        if (asChild) {
            const childProps = { ...props, disabled };
            return (
                <Slot
                    className={cn(buttonVariants({ variant, size, className }))}
                    ref={ref}
                    {...childProps}
                >
                    {children}
                </Slot>
            );
        }

        return (
            <button
                className={cn(
                    buttonVariants({ variant, size, className }),
                    loading && 'disabled:opacity-100',
                )}
                ref={ref}
                disabled={disabled || loading}
                aria-busy={loading || undefined}
                data-loading={loading ? '' : undefined}
                {...props}
            >
                {loading ? <Spinner size="xs" label={null} className="text-current" /> : null}
                {children}
            </button>
        );
    },
);
Button.displayName = 'Button';

export { Button };
